import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/shared/integrations/supabase/client";

/** Flat photo row — matches the shape PropertyAllPhotosGallery already consumes. */
export interface ReportAssetPhoto {
  id: string;
  asset_id: string;
  document_name: string;
  file_path: string;
  category: string;
  asset_name: string;
  asset_category: string;
  created_at: string;
  url: string;
}

/** Spec columns we surface in the right panel. Anything not listed is ignored. */
export type AssetSpecs = {
  asset_type?: string | null;
  manufacturer?: string | null;
  model_name?: string | null;
  model_number?: string | null;
  serial_number?: string | null;
  description?: string | null;
  manufacture_date?: string | null;
  estimated_age_years?: number | null;
  age_source?: string | null;
  amperage?: string | null;
  voltage?: string | null;
  phase?: string | null;
  wires?: string | null;
  btu_capacity?: string | null;
  capacity_gallons?: number | null;
  pressure_psi?: number | null;
  frequency_hz?: number | null;
  power_kw?: number | null;
  efficiency_rating?: string | null;
  fuel_type?: string | null;
  airflow_cfm?: number | null;
  gas_cfh_low_diff?: number | null;
  gas_cfh_high_diff?: number | null;
  condition?: string | null;
  condition_status?: string | null;
  warranty_expires?: string | null;
};

export interface ReportAsset {
  id: string;
  name: string;
  category: string;
  specs: AssetSpecs;
  photos: ReportAssetPhoto[]; // ascending by created_at
}

const ASSET_SELECT = [
  "id",
  "name",
  "asset_categories(name)",
  "manufacturer",
  "model_name",
  "model_number",
  "serial_number",
  "description",
  "manufacture_date_string",
  "estimated_age_years",
  "age_source",
  "condition_status",
].join(", ");

function resolveUrl(filePath: string | null | undefined): string {
  if (!filePath) return "/placeholder.svg";
  if (filePath.startsWith("http://") || filePath.startsWith("https://")) {
    return filePath;
  }
  const { data } = supabase.storage.from("asset-documents").getPublicUrl(filePath);
  return data?.publicUrl || "/placeholder.svg";
}

function getAssetCategory(asset: any): string {
  return asset?.asset_categories?.name ?? "Other";
}

export function usePropertyReportPhotos(propertyId: string | null | undefined) {
  const [photos, setPhotos] = useState<ReportAssetPhoto[]>([]);
  const [assets, setAssets] = useState<ReportAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchAll = useCallback(async () => {
    if (!propertyId) {
      setPhotos([]);
      setAssets([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);

      const { data: assetRows, error: assetsError } = await supabase
        .from("property_assets")
        .select(ASSET_SELECT)
        .eq("property_id", propertyId);
      if (assetsError) throw assetsError;
      if (!assetRows || assetRows.length === 0) {
        setPhotos([]);
        setAssets([]);
        setLoading(false);
        return;
      }

      const assetIds = assetRows.map((a: any) => a.id);

      const { data: docs, error: docsError } = await supabase
        .from("asset_documents")
        .select("id, asset_id, document_name, file_path, category, created_at")
        .is("deleted_at", null)
        .eq("document_type", "photo")
        .in("asset_id", assetIds)
        .order("created_at", { ascending: false });
      if (docsError) throw docsError;

      const assetMap = new Map<string, any>(assetRows.map((a: any) => [a.id, a]));

      const flatPhotos: ReportAssetPhoto[] = (docs ?? []).map((d: any) => {
        const a = assetMap.get(d.asset_id);
        return {
          id: d.id,
          asset_id: d.asset_id,
          document_name: d.document_name,
          file_path: d.file_path,
          category: d.category,
          asset_name: a?.name ?? "Unknown Asset",
          asset_category: getAssetCategory(a),
          created_at: d.created_at,
          url: resolveUrl(d.file_path),
        };
      });

      const photosByAsset = new Map<string, ReportAssetPhoto[]>();
      for (const p of flatPhotos) {
        const list = photosByAsset.get(p.asset_id) ?? [];
        list.push(p);
        photosByAsset.set(p.asset_id, list);
      }

      const grouped: ReportAsset[] = assetRows.map((a: any) => {
        const photosAsc = (photosByAsset.get(a.id) ?? [])
          .slice()
          .sort((x, y) => x.created_at.localeCompare(y.created_at));
        const { id, name, asset_categories, manufacture_date_string, ...rest } = a;
        return {
          id,
          name,
          category: asset_categories?.name ?? "Other",
          specs: {
            ...rest,
            manufacture_date: manufacture_date_string,
          } as AssetSpecs,
          photos: photosAsc,
        };
      });

      setPhotos(flatPhotos);
      setAssets(grouped);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Failed to fetch photos"));
      setPhotos([]);
      setAssets([]);
    } finally {
      setLoading(false);
    }
  }, [propertyId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  return useMemo(
    () => ({ photos, assets, loading, error, refetch: fetchAll }),
    [photos, assets, loading, error, fetchAll]
  );
}
